import { useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Printer,
  Copy,
  Star,
  AlertTriangle,
  XCircle,
  Check,
  Shield,
  SmilePlus,
  MessageSquare,
  Phone,
} from "lucide-react";
import { Button } from "../../ui/button";
import { useSelector } from "react-redux";
import {
  getCsatSubmission,
  getFeedbackForm,
  getProfileForm,
  getWhiteLabelLogos,
} from "../../../redux/reducer/incbotFeedbackReducer";
import logo from "../../../assets/images/incruiter-main-logo.svg";
import { formatDate, formatDuration, getAccessToken } from "../../../utils/helper";
import { Tooltip } from "react-tooltip";

const ProfileHeader = ({ shouldHide }: { shouldHide?: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { interviewId = "", roomId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const token = getAccessToken;
  const routeType = location.pathname.split("/").filter(Boolean)[0] ?? "";
  const profileForm = useSelector(getProfileForm);
  const feedbackForm = useSelector(getFeedbackForm);
  const whiteLabelLogos = useSelector(getWhiteLabelLogos);
  const csatSubmission = useSelector(getCsatSubmission);
  const [copied, setCopied] = useState(false);

  const handleRateInterview = () => {
    if (csatSubmission) return;
    const expires = searchParams.get("expires") || "";
    const signature = searchParams.get("signature") || "";
    const params = new URLSearchParams();
    if (expires) params.set("expires", expires);
    if (signature) params.set("signature", signature);
    const query = params.toString();
    navigate(`/csat/${interviewId}/${roomId}${query ? `?${query}` : ""}`, {
      state: routeType,
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1000);
      })
      .catch((err) => console.error("Failed to copy link:", err));
  };

  return (
    <div className="bg-white border-none border border-gray-200 p-3 md:p-6">
      <div className="flex flex-col gap-2 w-full align-center justify-center">
        {/* Mobile view */}
        <div className="sm:hidden flex-shrink-0 w-full flex justify-center mb-2">
          <img
            src={whiteLabelLogos?.logo ? whiteLabelLogos?.logo : logo}
            alt="InCruiter"
            className="h-10 w-20 object-contain md:h-12"
          />
        </div>
        <div className="flex gap-2 sm:hidden">
          <div className="flex flex-col gap-2 items-center justify-center">
            <div className="size-28 md:size-32 flex-shrink-0 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
              {profileForm?.candidateImage ? (
                <img src={profileForm?.candidateImage} className="w-full h-full object-cover" />
              ) : (
                <>
                  {profileForm?.name
                    ?.split(" ")
                    .map((n: any) => n[0])
                    .join("")}
                </>
              )}
            </div>
          </div>

          {/* Candidate info */}
          <div className="flex-1 overflow-hidden">
            <div className="group relative">
              <h1
                className="text-sm md:text-2xl font-bold text-gray-900 "
                title={profileForm?.name || ""}
              >
                {profileForm?.name || ""}
              </h1>
            </div>
            <div className="group relative">
              <span className="text-xs md:text-lg text-gray-500 " title={profileForm?.code || ""}>
                {profileForm?.code || ""}
              </span>
            </div>
            <div className="group relative">
              <p
                className="text-gray-700 text-[9px] md:text-md font-medium "
                title={`${profileForm?.position || ""} • ${profileForm?.company || ""}`}
              >
                {profileForm?.position || ""} • {profileForm?.company || ""}
              </p>
            </div>
            <div className="group relative">
              <p
                className="text-gray-500 text-[9px] md:text-sm"
                title={`${profileForm?.email || ""} • ${
                  profileForm?.phone?.replace(/\+\+/g, "+") || ""
                }`}
              >
                {profileForm?.email || ""} • {profileForm?.phone?.replace(/\+\+/g, "+") || ""}
              </p>
            </div>
            {/* [2026-10-05] CHANGED — INC-996 (INC-1627): this row can now hold a third item
                (Interview Language), so it may wrap instead of being clipped — but ONLY when that
                item is present. Without it the class string is exactly the old one.
                Old: <div className="flex items-center gap-2 max-sm:mb-2"> */}
            <div
              className={
                profileForm?.interviewLanguage
                  ? "flex items-center gap-2 max-sm:mb-2 flex-wrap"
                  : "flex items-center gap-2 max-sm:mb-2"
              }
            >
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-[9px]">
                  {formatDate(profileForm?.interviewDate, 0)}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-[9px]">
                  {!shouldHide &&
                    formatDuration(feedbackForm?.totalDuration || profileForm?.duration || 0)}
                </span>
              </div>
              {/* [2026-10-05] ADDED — INC-996 (INC-1627): the language this IncScreen interview ran
                  in, shown exactly like the IncBot report does. `interviewLanguage` is absent on
                  older interviews, and then nothing is rendered here. */}
              {profileForm?.interviewLanguage && (
                <div className="flex items-center space-x-2 text-gray-600">
                  <span className="text-sm max-sm:text-[9px]">
                    Interview Language: {profileForm.interviewLanguage}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop view */}
        <div className="flex justify-between items-center max-sm:hidden">
          <div className="size-24 md:size-32 flex-shrink-0 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
            {profileForm?.candidateImage ? (
              <img src={profileForm?.candidateImage} className="w-full h-full object-cover" />
            ) : (
              <>
                {profileForm?.name
                  ?.split(" ")
                  .map((n: any) => n[0])
                  .join("")}
              </>
            )}
          </div>
          <div className="min-w-0 sm:block max-sm:hidden flex-1 ml-4 overflow-hidden">
            <div className="flex items-center space-x-2 mb-2 flex-wrap">
              <div className="truncate group relative">
                <h1
                  className="text-xl md:text-2xl font-bold text-gray-900 truncate"
                  title={profileForm?.name || ""}
                >
                  {profileForm?.name || ""}
                </h1>
                <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                  {profileForm?.name || ""}
                </span>
              </div>
              <div className="truncate group relative">
                <span
                  className="text-md md:text-lg text-gray-500 truncate"
                  title={profileForm?.code || ""}
                >
                  {profileForm?.code || ""}
                </span>
                <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                  {profileForm?.code || ""}
                </span>
              </div>
            </div>
            <div className="truncate group relative">
              <p
                className="text-gray-700 text-sm md:text-md font-medium truncate"
                title={`${profileForm?.position || ""} • ${profileForm?.company || ""}`}
              >
                {profileForm?.position || ""} • {profileForm?.company || ""}
              </p>
              <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                {profileForm?.position || ""} • {profileForm?.company || ""}
              </span>
            </div>
            <div className="truncate group relative">
              <p
                className="text-gray-500 text-xs md:text-sm truncate"
                title={`${profileForm?.email || ""} • ${
                  profileForm?.phone?.replace(/\+\+/g, "+") || ""
                }`}
              >
                {profileForm?.email || ""} • {profileForm?.phone?.replace(/\+\+/g, "+") || ""}
              </p>
              <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                {profileForm?.email || ""} • {profileForm?.phone?.replace(/\+\+/g, "+") || ""}
              </span>
            </div>
            {/* [2026-10-05] CHANGED — INC-996 (INC-1627): same as the mobile row above — may wrap
                (as the IncBot report's row does) ONLY when Interview Language is present.
                Without it the class string is exactly the old one.
                Old: <div className="flex gap-3"> */}
            <div className={profileForm?.interviewLanguage ? "flex gap-3 flex-wrap" : "flex gap-3"}>
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-xs">
                  {formatDate(profileForm?.interviewDate, 0)}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-xs">
                  {!shouldHide &&
                    formatDuration(feedbackForm?.totalDuration || profileForm?.duration || 0)}
                </span>
              </div>
              {/* [2026-10-05] ADDED — INC-996 (INC-1627): Interview Language, same look as the
                  IncBot report. Absent on older interviews → nothing rendered. */}
              {profileForm?.interviewLanguage && (
                <div className="flex items-center space-x-2 text-gray-600">
                  <span className="text-sm max-sm:text-xs">
                    Interview Language: {profileForm.interviewLanguage}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="max-sm:hidden flex-shrink-0">
            <img
              src={whiteLabelLogos?.logo ? whiteLabelLogos?.logo : logo}
              alt="InCruiter"
              className="h-14 w-32 object-contain"
            />
          </div>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="flex items-center justify-between mt-2 md:mt-6 max-sm:flex-col">
        <div className="flex items-center space-x-3 max-sm:w-full max-sm:mb-2">
          {profileForm?.status && (
            <div
              className={`flex items-center space-x-2 px-3 py-1 rounded-full
                ${
                  profileForm.status === "Qualified"
                    ? "bg-green-50 text-green-700"
                    : "bg-red-50 text-red-700"
                }
              `}
            >
              {profileForm.status === "Qualified" && <Star className="w-4 h-4" />}
              {profileForm.status === "Disqualified" && <XCircle className="w-4 h-4" />}
              <span className="text-sm font-medium">{profileForm.status}</span>
            </div>
          )}
          <div
            className={`flex items-center space-x-2 px-3 py-1 rounded-full
                ${"bg-green-50 text-green-700"}
              `}
          >
            {!shouldHide ? <Phone className="w-4 h-4" /> : <Star className="w-4 h-4" />}
            <span className="text-sm font-medium">{!shouldHide ? "Call" : "Whatsapp"}</span>
          </div>

          {profileForm?.deepfakeDetected !== undefined && !profileForm?.deepfakeDetected ? (
            <div
              className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
                profileForm.deepfakeDetected
                  ? "bg-red-50 text-red-700"
                  : "bg-green-50 text-green-700"
              }`}
            >
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">
                {profileForm.deepfakeDetected ? "Deepfake Detected" : "Deepfake Not Detected"}
              </span>
            </div>
          ) : (
            <>
              {profileForm?.deepFakeAudioDetection && (
                <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-red-50 text-red-700">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">Deepfake Audio Detected</span>
                </div>
              )}

              {profileForm?.deepFakeVideoDetection && (
                <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-red-50 text-red-700">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">Deepfake Video Detected</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center space-x-2 max-sm:w-full">
          {/* {token && (
            <span
              data-tooltip-id="rate-interview-tooltip"
              data-tooltip-content={
                csatSubmission
                  ? "CSAT feedback already submitted successfully"
                  : "CSAT Pending"
              }
              data-tooltip-place="top"
              className="inline-block"
            >
              <Button
                onClick={handleRateInterview}
                variant="outline"
                size="sm"
                disabled={csatSubmission}
                className="pr-11 print:hidden max-sm:w-1/2"
              >
                <SmilePlus />
                Rate This Interview
              </Button>
            </span>
          )} */}

          {/* <Tooltip id="rate-interview-tooltip" /> */}
          <Button
            onClick={() => window.print()}
            variant="outline"
            size="sm"
            className="pr-11 print:hidden max-sm:w-1/2"
          >
            <Printer className="w-4 h-4" />
            Print
          </Button>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            size="sm"
            className="print:hidden max-sm:w-1/2"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? "Link copied" : "Copy Link"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
